/**
 * Attribution / UTM persistence layer.
 *
 * Captures UTM parameters on first visit and stores them for the entire
 * browser session (sessionStorage) plus a 30-day cookie so multi-tab and
 * return visits within the campaign window stay attributed.
 *
 * Used by useAnalytics to auto-attach UTMs to every analytics_events row,
 * and by lead-capture surfaces (First500, Launch waitlist) to stamp leads.
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const SESSION_KEY = 'amorette_utm';
const LANDING_KEY = 'amorette_landing_page';
const COOKIE_KEY = 'amorette_utm';
const COOKIE_DAYS = 30;

const setCookie = (name: string, value: string, days: number) => {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2] ?? '') : null;
};

const safeJsonParse = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

/**
 * Reads UTM params from the current URL. Returns only keys that are present.
 */
export const readUtmFromUrl = (): UtmParams => {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const result: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) result[key] = value.slice(0, 200);
  }
  return result;
};

/**
 * Captures UTMs from the URL (if any) and persists them for the session +
 * 30 days. Should be called once at app boot.
 *
 * First-touch wins: if UTMs already exist in storage, the new URL UTMs
 * overwrite them ONLY when at least utm_source is present in the URL.
 * This means an organic re-visit doesn't wipe a prior campaign attribution.
 */
export const captureAttribution = (): UtmParams => {
  if (typeof window === 'undefined') return {};

  const fromUrl = readUtmFromUrl();
  const existing = getStoredUtm();

  // Persist landing page (first page in session)
  if (!sessionStorage.getItem(LANDING_KEY)) {
    sessionStorage.setItem(LANDING_KEY, window.location.pathname + window.location.search);
  }

  if (fromUrl.utm_source || fromUrl.utm_campaign) {
    const serialized = JSON.stringify(fromUrl);
    sessionStorage.setItem(SESSION_KEY, serialized);
    setCookie(COOKIE_KEY, serialized, COOKIE_DAYS);
    return fromUrl;
  }

  return existing;
};

/**
 * Returns persisted UTM params (session takes priority, then cookie fallback).
 */
export const getStoredUtm = (): UtmParams => {
  if (typeof window === 'undefined') return {};
  const fromSession = safeJsonParse<UtmParams>(sessionStorage.getItem(SESSION_KEY));
  if (fromSession && Object.keys(fromSession).length > 0) return fromSession;
  const fromCookie = safeJsonParse<UtmParams>(getCookie(COOKIE_KEY));
  return fromCookie ?? {};
};

/**
 * Returns the first page the user landed on this session.
 */
export const getLandingPage = (): string | null => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(LANDING_KEY);
};

/**
 * Convenience: returns UTMs flattened into a record suitable for direct
 * insertion into the early_access_leads / analytics_events tables.
 */
export const getAttributionPayload = () => {
  const utm = getStoredUtm();
  return {
    utm_source: utm.utm_source ?? null,
    utm_medium: utm.utm_medium ?? null,
    utm_campaign: utm.utm_campaign ?? null,
    utm_content: utm.utm_content ?? null,
    utm_term: utm.utm_term ?? null,
    landing_page: getLandingPage(),
  };
};
